import {
  getCarpetYardsForJob,
  getCarpetFurniturePiecesForJob,
  getCarpetStepsForJob,
} from "./installerCapacityRemaining";

// - - - - - - - - - -
// Three-tier filler-job recommender for issue #140.
//
// When an installer's day has partial carpet capacity (some carpet jobs
// already assigned but the daily cap not yet hit), this helper surfaces
// candidate jobs that COULD fill the gap. The popover shows the result
// of this function as two visually separated sections:
//
//   🏃🏽‍♂️ Tier 1 — EXPEDITE pull-forward candidates
//     Carpet jobs already ASSIGNED (statusCode 4) to ANY installer at the
//     cell installer's home location, scheduled on a future date STRICTLY
//     LATER than the red cell's date, marked `expediteFlag === true`, AND
//     with `rtsDate <= cellDate` (material is ready by the day we'd be
//     moving the job to — Brandon's "Under no circumstances" rule, added
//     2026-05-20). Missing/unparseable rtsDate BLOCKS the candidate.
//     These are the highest-value recommendations because:
//       (a) the customer has explicitly signaled "move me up if possible,"
//       (b) pulling forward simultaneously fills the current gap AND opens
//           the later day for bigger work,
//       (c) location-and-installer eligibility is already proven (someone
//           who can work this location is already assigned),
//       (d) the material has arrived (rtsDate gate confirms physical
//           readiness — no point recommending a move-up that can't
//           actually happen).
//     Brandon's primary use case (uncovered 2026-05-19 during scope
//     review): "If installer has 50-yd carpet tomorrow + 50-yd carpet
//     EXPEDITE Friday, both red — top recommendation on tomorrow's
//     popover is the Friday expedite job."
//
//   🔴 Tier 2 — RTS unassigned candidates
//     Carpet jobs with statusCode 3 (Ready-To-Schedule), no installer,
//     same brand, located within the cell installer's eligible-location set
//     (their home + Additional_Locations). Brandon called these out as
//     secondary — useful but less interesting than the pull-forward case.
//
//   🔧 Tier 3 — OPEN REPAIRS (issue #145, 2026-05-20; refined 2026-05-20
//     for #140 follow-up — Brandon bug invoice 2520748)
//     ActiveQueue docs with `classification === "Repair Order"` where
//     `installer === cellInstaller.payrollNo` AND `statusCode` is in the
//     "still legitimately doable" allow-set: 3, 3.1, 4, 30, 30.1.
//
//     The discriminator is CLASSIFICATION (not statusCode). Original spec
//     keyed on statusCode 30 only, but Brandon's UAT testing surfaced
//     two bugs:
//       • invoice 2520748 — Repair Order at statusCode 3 (RTS) assigned
//         to installer 3895.01, silently dropped by strict-30 filter
//       • invoice 2520769 — Repair Order in "Created" status (1.x)
//         assigned to installer 3700.01, silently dropped by strict-30
//     Per Leland's spec (2026-05-20): "We want recommendations for
//     anything that they could possibly legitimately do. They can do
//     anything in the RTS column, and till I'm told otherwise they can
//     do it even if it's suppressed, they can't do it if it's canceled,
//     they can't do it if it's already done which it should be anything
//     over a status code of four, except for 30 which is a repair open,
//     30.1 which is open, but suppressed."
//
//     Implemented as an EXCLUSION list (not an allow-list) so any future
//     "Created/Verified/etc." status the installer could still act on
//     stays eligible by default:
//       • EXCLUDE: statusCode === 20 (Cancelled)
//       • EXCLUDE: statusCode > 4 except for {30, 30.1}
//         (excludes 5-14.x in-progress, 15 Paid, 21 Write-Off, 31
//         Repair-Completed, etc. — anything "already done")
//     Everything else included (Created 1.x, Verified 2.x, RTS 3, RTS-
//     suppressed 3.1, Scheduled 4, Repair-Open 30, Repair-Open-
//     suppressed 30.1).
//
//     Synthetic yards = 1 — used for popover display only. As of the 2026-
//     05-20 #140 conflict resolution, Tier 3 is FULLY INDEPENDENT of yards,
//     furniture, and steps capacity. The only gate on Tier 3 beyond
//     classification + installer + status-exclusion is the repair-stops
//     hard cap (`remainingRepairStops < 1` blocks all repairs). Per
//     Leland's spec: "if they've got 12 jobs scheduled for a day, and their
//     yardage is 2 yards for once a day and 2 yards for twice a day, we
//     still want to show recommendations for possible repair orders to
//     also schedule that day. The only restriction on repair orders that
//     we have currently is how many repair orders per day are allowed with
//     the new stops value." Repairs of ANY surface qualify. Furniture/
//     steps piece counts are still computed (real values from
//     InvoiceLineItems SKU matches) for popover row DISPLAY but do not
//     gate eligibility.
//
// Both tiers are filtered to jobs whose carpet yardage <= remainingYds and
// ranked CLOSEST-FIT (largest qty first within each tier). Closest-fit packs
// the day tightest; the install manager can scan down to smaller jobs if
// the top suggestion doesn't work for other reasons.
//
// Scope decisions (resolved 2026-05-19 with Brandon via Leland):
//   • Read-only v1 — clicking a row in the popover does NOT initiate a move.
//     The install manager closes the popover and re-assigns manually via
//     drag-drop. Click-to-action is a v1.1 scope expansion.
//   • Same-installer + same-location for expedite tier — Brandon's framing
//     was about pulling forward work WITHIN ONE LOCATION's day. Surfacing
//     cross-location expedite jobs from other locations would imply we're
//     suggesting geographic re-routing, which is a separate UX call.
//   • Location eligibility for the RTS tier mirrors the Lambda
//     `getProjectsByCompanyWithShared` query: case-insensitive substring
//     match of `Additional_Locations` (which is a single string field,
//     possibly comma-separated, possibly empty). Mirrors the production
//     pattern at modelRW.context.js:3214.
//   • Per-tier closest-fit ranking with stable secondary tiebreak on
//     invoiceNo (numeric ASC) so repeated calls produce a deterministic
//     order regardless of input ordering.

// - - - - - - - - - -
// Private helpers.

// Mirrors the Lambda's regex pattern $regex: locationFilter, $options: "i".
// `Additional_Locations` is a string field on Candidates that may be empty,
// a single location code, or a comma-separated list. Case-insensitive
// substring match captures all forms cleanly.
const installerCanWorkLocation = (candidate, location) => {
  if (!candidate || !location) return false;
  if (candidate.cLocationCode === location) return true;
  const additional = candidate.Additional_Locations;
  if (!additional || typeof additional !== "string") return false;
  return additional.toUpperCase().includes(location.toUpperCase());
};

// #149 (2026-05-21) — capacity_stores filter (per-installer store checklist).
//
// Brandon's spec (issue #149, 2026-05-20): each installer gets a per-
// installer list of Lowe's store numbers they've agreed to work for.
// All stores at their primary Keller location default to "checked"
// (allowed). Install managers can uncheck individual stores per
// installer if the installer has refused/can't work that store. Jobs
// originating from unchecked stores must NOT be recommended for that
// installer.
//
// Field name: `capacity_stores` (Leland confirmed 2026-05-21; matches the
// `capacity_*` naming family used elsewhere in the Candidates schema —
// capacity_carpet_once/twice, capacity_stops_jobs/repairs, etc.).
// John is adding it to Recruiter; Leland is adding it to the outgoing
// Recruiter → MongoDB payload + the `candidateProjection` in the queries
// Lambda. Until both upstream steps complete, this helper sees `undefined`
// for every candidate and behaves as a no-op (ALL allowed — see safe
// transition default below).
//
// Storage format: this helper accepts BOTH forms gracefully so whatever
// shape John lands on works without code changes:
//   - Array: [2517, "2464", 1234]
//   - String: "2517,2464,1234" or "2517\r2464" (Recruiter's
//     Additional_Locations uses \r-separated strings, so plausible)
//
// "Not configured" safe transition default: when `capacity_stores` is
// missing / null / undefined / empty-string / empty-array on the
// Candidate, ALL stores are treated as allowed. This matches Brandon's
// spec ("all stores will be checked as available as default") and
// mirrors the same pattern used by `capacity_stops_*` (#146): a missing
// field means no constraint kicks in.
//
// Scope: applied to Tier 1 (Expedite pull-forward) + Tier 2 (RTS
// unassigned). Tier 3 (Open Repairs) is NOT filtered by this gate —
// repairs are already assigned to this installer, so the store check
// is moot ("the installer already agreed to do this one"). If Brandon
// later wants Tier 3 included, the filter is a one-line addition to
// the Tier 3 branch.
const installerHasApprovedStore = (candidate, storeNo) => {
  if (!candidate) return true; // defensive — let other gates decide
  const approved = candidate.capacity_stores;
  // Missing/null/undefined → ALL allowed (transition default)
  if (approved === undefined || approved === null) return true;

  // Normalize the target storeNo to a string for comparison. ActiveQueue
  // typically stores lowes_storeNo as a string like "2517"; can also be
  // numeric or empty. If empty/missing on the job, we can't filter on it
  // — allow the recommendation through (defensive).
  if (storeNo === undefined || storeNo === null || storeNo === "") return true;
  const targetStr = String(storeNo).trim();

  let list;
  if (Array.isArray(approved)) {
    list = approved;
  } else if (typeof approved === "string") {
    // Split on common separators: comma, \r, \n, whitespace
    const trimmed = approved.trim();
    if (trimmed === "") return true; // empty string → ALL allowed
    list = trimmed.split(/[,\r\n\s]+/).filter((s) => s.length > 0);
  } else {
    // Unexpected shape → defensively allow (matches "not configured" semantics)
    return true;
  }

  if (list.length === 0) return true; // empty list → ALL allowed

  // Compare as strings, trimmed, ignoring leading zeros so "2517" matches
  // 2517 numeric and " 2517 " whitespace variants.
  const normalizedList = list.map((s) => String(s).trim());
  return normalizedList.includes(targetStr);
};

// Returns true iff the date string represents a date strictly AFTER cellDate.
// Used to filter expedite candidates to FUTURE assignments only — we should
// never recommend "move a past job forward" or "move a same-day job to
// itself." Snaps to local midnight on both sides so a time-of-day input
// doesn't accidentally classify the same day as future.
const isStrictlyAfterDate = (dateString, cellDateString) => {
  if (!dateString || !cellDateString) return false;
  const dateMs = new Date(dateString).setHours(0, 0, 0, 0);
  const cellMs = new Date(cellDateString).setHours(0, 0, 0, 0);
  if (isNaN(dateMs) || isNaN(cellMs)) return false;
  return dateMs > cellMs;
};

// #140 follow-up (2026-05-20) — rtsDate gate for Tier 1 Expedite pull-forward.
//
// Brandon's spec: "Under no circumstances can a job be scheduled before this
// date." rtsDate = "Ready To Schedule" date on ActiveQueue docs, meaning the
// product/material has arrived and the job is physically schedulable. A
// future-scheduled expedite job can only be pulled forward to a cell if
// its rtsDate is at-or-before the cell date.
//
// Inclusive boundary per Brandon's quote: "if RTS date field is current date
// or earlier, then product is received."
//
// Missing/null/empty/unparseable → BLOCK (per Leland's 2026-05-20 decision;
// the conservative choice). Different from how we handled `expediteFlag`
// (strict-true, missing = false) and `capacity_stops_jobs` (missing =
// no-cap). For rtsDate the spec is "Under no circumstances" — so when we
// can't confirm material is ready, we don't recommend.
//
// Tier 2 (RTS unassigned) and Tier 3 (Repairs) do NOT call this gate:
//   - Tier 2: readiness is encoded in the statusCode 3 workflow stage already
//   - Tier 3: per Brandon, "for repairs, we should not look at either of
//     those fields. Recommend recommended should be any open repair order."
const isRtsReadyForCellDate = (doc, cellDateString) => {
  const rts = doc?.rtsDate;
  if (!rts || typeof rts !== "string") return false; // missing/null/unparseable type → BLOCK
  const rtsMs = new Date(rts).setHours(0, 0, 0, 0);
  if (isNaN(rtsMs)) return false; // unparseable date string → BLOCK
  const cellMs = new Date(cellDateString).setHours(0, 0, 0, 0);
  if (isNaN(cellMs)) return false;
  return rtsMs <= cellMs;
};

// Convert a raw ActiveQueue doc into a slim popover-row entry. Keeps
// enough fields for display + a `rawDoc` escape hatch for future click-to-
// action work. invoiceNo is normalized to a number when possible to make
// numeric sorting on ties deterministic.
const toPopoverEntry = (doc, carpetYds, furniturePieces, stepPieces) => {
  const invoiceNumeric = parseFloat(doc.invoiceNo);
  return {
    _id: doc._id,
    invoiceNo: doc.invoiceNo,
    invoiceNoNumeric: isNaN(invoiceNumeric) ? null : invoiceNumeric,
    customerFirstName: doc.customer_firstName || "",
    customerLastName: doc.customer_lastName || "",
    location: doc.location,
    // Lowe's store identifier — added 2026-05-19 (UAT review #4). Install
    // managers want to see the originating store on each recommended row.
    // Defaults to empty string when missing so the row renderer's
    // `storeNo > 0` check naturally hides the badge for jobs that lack it
    // (older docs, SCG jobs, etc.).
    storeNo: doc.lowes_storeNo || "",
    carpetYds,
    furniturePieces,
    stepPieces,
    installer: doc.installer || "",
    dateScheduled: doc.date_scheduled || "",
    surface: doc.surface,
    expedite: doc.expediteFlag === true,
    statusCode: parseFloat(doc.statusCode),
    rawDoc: doc,
  };
};

// Closest-fit ranking — largest qty first. Stable secondary sort on
// invoiceNoNumeric (ASC) so a tie produces deterministic output.
const closestFitCompare = (a, b) => {
  if (a.carpetYds !== b.carpetYds) return b.carpetYds - a.carpetYds;
  // null invoiceNoNumeric goes to the end so deterministic-but-unparseable
  // invoiceNos don't reorder unpredictably.
  if (a.invoiceNoNumeric === null && b.invoiceNoNumeric === null) return 0;
  if (a.invoiceNoNumeric === null) return 1;
  if (b.invoiceNoNumeric === null) return -1;
  return a.invoiceNoNumeric - b.invoiceNoNumeric;
};

// - - - - - - - - - -
// Public helper.

/**
 * Builds the two-tier recommendation list for a red partial-capacity cell.
 *
 * @param {Object} params
 * @param {Object} params.cellInstallerCandidate — the installer whose cell
 *                                                 is red. Must include
 *                                                 `payrollNo`, `cLocationCode`,
 *                                                 `Additional_Locations`,
 *                                                 `companyCode`.
 * @param {string} params.cellDate              — the date of the red cell,
 *                                                 "M/D/YYYY" format.
 * @param {number} params.remainingYds          — carpet yards remaining on
 *                                                 the cell day; jobs > this
 *                                                 won't fit.
 * @param {number} [params.remainingFurniture]  — furniture-piece capacity
 *                                                 remaining; jobs whose
 *                                                 furniture count exceeds
 *                                                 this are excluded. If
 *                                                 omitted, furniture is not
 *                                                 a constraint (treats as
 *                                                 +∞). Pass `0` to enforce
 *                                                 "no furniture-containing
 *                                                 jobs allowed."
 * @param {number} [params.remainingSteps]      — steps-piece capacity
 *                                                 remaining; same semantics
 *                                                 as remainingFurniture.
 * @param {number} [params.remainingJobStops]    — jobs-stops capacity
 *                                                 remaining (#146). HARD CAP:
 *                                                 when 0, ALL job tier
 *                                                 candidates (Tier 1 Expedite
 *                                                 + Tier 2 RTS) are
 *                                                 excluded. `Infinity` (or
 *                                                 omitted) = no cap enforced
 *                                                 (the safe default before
 *                                                 FileMaker delivery).
 * @param {number} [params.remainingRepairStops] — repairs-stops capacity
 *                                                 remaining (#146). HARD CAP:
 *                                                 when 0, ALL Tier 3 repair
 *                                                 candidates are excluded.
 * @param {Array}  params.activeQueueDocs       — raw ActiveQueue docs
 *                                                 (filteredProjectsList).
 * @param {Array}  [params.furnitureSkuList]    — JOB_FURNITURE_SKULIST. If
 *                                                 omitted or empty, no line
 *                                                 items match → every job's
 *                                                 furniture count is 0 →
 *                                                 furniture constraint
 *                                                 always passes.
 * @param {Array}  [params.stepsSkuList]        — JOB_STEPS_SKULIST. Same
 *                                                 inert-when-empty semantics.
 * @param {Function} [params.normalizePayroll]  — optional payroll formatter.
 *
 * @returns {Object} {
 *   expediteJobs:         Array<popoverEntry>, — Tier 1, ranked closest-fit (yards)
 *   rtsJobs:              Array<popoverEntry>, — Tier 2, ranked closest-fit (yards)
 *   alreadyScheduledJobs: Array<popoverEntry>, — Tier 4 (issue #156, 2026-05-22),
 *                                              non-expedite future-dated jobs
 *                                              (typically FAV auto-pre-scheduled
 *                                              at status 1.2) that could be
 *                                              pulled forward into this cell
 *   repairJobs:           Array<popoverEntry>, — Tier 3 (issue #145, 2026-05-20),
 *                                              open repairs assigned to this
 *                                              installer; synthetic carpetYds=1
 *   totalCount:           number,              — sum of all four tier lengths
 * }
 *
 * Each popover entry includes `carpetYds`, `furniturePieces`, and `stepPieces`
 * so the popover row renderer can show all three dimensions to the install
 * manager.
 */
export function recommendFillerCarpetJobs({
  cellInstallerCandidate,
  cellDate,
  remainingYds,
  remainingFurniture,
  remainingSteps,
  remainingJobStops,
  remainingRepairStops,
  activeQueueDocs,
  furnitureSkuList,
  stepsSkuList,
  normalizePayroll,
}) {
  const empty = {
    expediteJobs: [],
    rtsJobs: [],
    alreadyScheduledJobs: [],
    repairJobs: [],
    totalCount: 0,
  };

  if (!cellInstallerCandidate || !cellDate) return empty;
  if (!Array.isArray(activeQueueDocs)) return empty;

  // Furniture/steps fit gates: treat undefined / NaN as "no constraint"
  // (+∞). Caller can pass 0 to enforce strict "no furniture/steps allowed."
  const parseConstraint = (v) => {
    if (v === undefined || v === null) return Infinity;
    const n = parseFloat(v);
    return isNaN(n) ? Infinity : n;
  };
  const safeFurnitureRemaining = parseConstraint(remainingFurniture);
  const safeStepsRemaining = parseConstraint(remainingSteps);
  // #146 — stops fit gates. Each Tier 1/Tier 2 entry consumes 1 jobs-stop;
  // each Tier 3 entry consumes 1 repairs-stop. When remaining is Infinity
  // (the "not configured" default) the gate is a no-op. When remaining is
  // a finite number, the gate excludes the entire tier if remaining < 1
  // (HARD CAP).
  const safeJobStopsRemaining = parseConstraint(remainingJobStops);
  const safeRepairStopsRemaining = parseConstraint(remainingRepairStops);
  const noMoreJobsAllowed = safeJobStopsRemaining < 1;
  const noMoreRepairsAllowed = safeRepairStopsRemaining < 1;

  // 2026-05-20 (#140 conflict resolution) — yards capacity now gates Tier 1+2
  // ONLY. Tier 3 (Repairs) runs independently of yards because repairs use
  // synthetic yardage and consume a separate `capacity_stops_repairs` cap.
  // Per Leland's spec: "we still want to show recommendations for possible
  // repair orders to also schedule that day" even when jobs side is exhausted
  // (e.g. 12 jobs, 2-yard cap, but repair-stops capacity remaining).
  const parsedRemainingYds = parseFloat(remainingYds);
  const safeRemaining = isNaN(parsedRemainingYds) ? 0 : parsedRemainingYds;
  const jobsTierBlockedByYards = safeRemaining <= 0;

  // If BOTH job-side (yards) AND repair-side (stops) are fully exhausted,
  // there's nothing to recommend at all — return early. Otherwise, continue
  // and let each tier's individual gate decide eligibility.
  if (jobsTierBlockedByYards && noMoreRepairsAllowed) return empty;

  const companyCode = cellInstallerCandidate.companyCode;
  const homeLocation = cellInstallerCandidate.cLocationCode;
  // 2026-05-21 (Brandon UAT bug #1) — payrollNo can be stored as a number
  // (e.g. 887.01) on the Candidates collection while ActiveQueue's
  // `installer` field is always a string with zero-padding (e.g.
  // "0887.01"). Strict `doc.installer !== installerPayroll` silently
  // dropped every Repair Order assigned to numeric-payroll installers.
  // installerCapacityRemaining.js already used normalizePayroll for the
  // cell-trigger; the recommender's Tier 3 branch was the missing
  // half. Defaults to identity if not provided.
  const normalize = normalizePayroll || ((v) => v);
  const installerPayroll = normalize(cellInstallerCandidate.payrollNo);

  const expediteJobs = [];
  const rtsJobs = [];
  const alreadyScheduledJobs = []; // #156 (2026-05-22) — Tier 4
  const repairJobs = [];

  for (const doc of activeQueueDocs) {
    if (!doc) continue;
    if (doc.companyCode !== companyCode) continue;

    const statusCode = parseFloat(doc.statusCode);

    // Tier 3 — OPEN REPAIRS (issue #145, 2026-05-20; refined 2026-05-20
    // for #140 follow-up — Brandon bug invoice 2520748). Branched first
    // because repairs use SYNTHETIC carpetYds=1 (Brandon's spec — repairs
    // are small enough that we don't gate on real material yardage) and
    // are NOT restricted to surface === "Carpet" (repairs of any surface
    // are eligible).
    //
    // Discriminator is CLASSIFICATION, not statusCode — a Repair Order
    // can be at status 3 (RTS), 3.1 (RTS-Suppressed), 4 (Scheduled), 30
    // (Repair-Open) or 30.1 (Repair-Open-Suppressed) and all are
    // "legitimately doable" per Leland's spec.
    if (doc.classification === "Repair Order") {
      if (normalize(doc.installer) !== installerPayroll) continue;
      // Status EXCLUSION list (not allow-list — Brandon UAT bugs 2520748
      // status 3 + 2520769 status 1.x both got dropped by the prior
      // allow-list). Exclude only Cancelled and "already done" statuses.
      const isExcluded =
        statusCode === 20 || // Cancelled
        (statusCode > 4 && statusCode !== 30 && statusCode !== 30.1); // already done — except Repair-Open variants
      if (isExcluded) continue;
      if (noMoreRepairsAllowed) continue; // #146 — repairs-stops hard cap
      // 2026-05-20 (#140 conflict resolution) — Tier 3 is INDEPENDENT of
      // yards / furniture / steps. Per Leland's spec: "The only restriction
      // on repair orders that we have currently is how many repair orders
      // per day are allowed with the new stops value." A repair fits in a
      // partial-capacity cell as long as the repair-stops cap has room.
      // We compute repairFurniture/repairSteps purely for popover display
      // (so the row UI can show real piece counts) — they don't gate.
      const repairYds = 1; // synthetic, for popover display only
      const repairFurniture = getCarpetFurniturePiecesForJob(doc, furnitureSkuList);
      const repairSteps = getCarpetStepsForJob(doc, stepsSkuList);
      repairJobs.push(toPopoverEntry(doc, repairYds, repairFurniture, repairSteps));
      continue;
    }

    // Tier 1 / Tier 2 / Tier 4 — carpet-only paths. 2026-05-20 (#140 conflict
    // resolution): when yards is exhausted (one-load clamp or full cap),
    // ALL job-tier recommendations are blocked even if Tier 3 repairs
    // remain eligible.
    if (jobsTierBlockedByYards) continue;
    if (doc.surface !== "Carpet") continue;

    const carpetYds = getCarpetYardsForJob(doc);
    if (carpetYds <= 0) continue; // not a sized carpet job → can't compare
    if (carpetYds > safeRemaining) continue; // yards doesn't fit

    // Compute per-job furniture + steps piece counts via SKU match on the
    // InvoiceLineItems array. Empty/missing SKU list → 0 pieces → no
    // constraint violation.
    const furniturePieces = getCarpetFurniturePiecesForJob(doc, furnitureSkuList);
    const stepPieces = getCarpetStepsForJob(doc, stepsSkuList);

    if (furniturePieces > safeFurnitureRemaining) continue; // furniture doesn't fit
    if (stepPieces > safeStepsRemaining) continue; // steps doesn't fit

    // #156 (2026-05-22, Leland precision spec) — Tier 1 and Tier 4 BOTH
    // accept exactly the two "currently rendered in scheduler cells with a
    // date" statuses:
    //
    //   1.2 — "Scheduled but not yet verified" (FAV auto-pre-scheduled state
    //         FileMaker stamps at invoice-create time, BEFORE a human has
    //         signed off on the date.)
    //   4   — "Scheduled" (committed assignment).
    //
    // Explicitly EXCLUDES the status-3.x range (RTS-suppressed family —
    // Leland: "we don't want any recommendations with suppressed jobs").
    // Status 3.0 itself is RTS unassigned and goes to Tier 2 below.
    //
    // Prior #140 implementation gated only on `statusCode === 4` and so
    // silently dropped the entire FAV-auto-scheduled population. Brandon's
    // example invoice 2520085 (status 1.2, unassigned, date_scheduled
    // 5/29/26, rtsDate 5/20/26) was the case that surfaced this.
    const isTier1Or4Status = statusCode === 1.2 || statusCode === 4;

    if (doc.expediteFlag === true && isTier1Or4Status) {
      // Tier 1 — Expedite pull-forward at the cell installer's home
      // location, ANY installer (cross-installer reassignment is valid for
      // an expedite job that's been auto-scheduled to a later date).
      // Includes statuses 1.2 (FAV auto-scheduled, unverified) and 4
      // (Scheduled, committed) — broadened 2026-05-22 per #156 so the
      // FAV-auto-scheduled expedite-flagged docs aren't silently dropped.
      if (doc.location !== homeLocation) continue;
      if (!isStrictlyAfterDate(doc.date_scheduled, cellDate)) continue;
      if (noMoreJobsAllowed) continue; // #146 — jobs-stops hard cap
      // 2026-05-20 — rtsDate gate. Block when material isn't ready by
      // cellDate (inclusive). Missing/unparseable → BLOCK.
      if (!isRtsReadyForCellDate(doc, cellDate)) continue;
      // #149 (2026-05-21) — capacity_stores filter.
      if (!installerHasApprovedStore(cellInstallerCandidate, doc.lowes_storeNo)) continue;
      expediteJobs.push(toPopoverEntry(doc, carpetYds, furniturePieces, stepPieces));
    } else if (statusCode === 3) {
      // Tier 2 — RTS unassigned at any eligible location for the installer
      // (home + Additional_Locations). Mirrors the Lambda's shared-installer
      // location-match logic. Status 3.x (suppressed RTS) excluded by the
      // strict-equality check (parseFloat('3.1') === 3.1, not 3).
      if (!installerCanWorkLocation(cellInstallerCandidate, doc.location)) continue;
      if (noMoreJobsAllowed) continue; // #146 — jobs-stops hard cap
      // #149 (2026-05-21) — capacity_stores filter.
      if (!installerHasApprovedStore(cellInstallerCandidate, doc.lowes_storeNo)) continue;
      rtsJobs.push(toPopoverEntry(doc, carpetYds, furniturePieces, stepPieces));
    } else if (isTier1Or4Status) {
      // Tier 4 — ALREADY SCHEDULED PULL FORWARD (issue #156, 2026-05-22).
      //
      // Non-expedite carpet Jobs at statuses 1.2 (FAV auto-pre-scheduled,
      // unverified) or 4 (Scheduled, committed) — NOT 3 (Tier 2's domain) —
      // with a populated date_scheduled in the future, eligible to be moved
      // earlier into THIS cell.
      //
      // Use case: FileMaker's FAV automated invoice flow creates docs at
      // statusCode=1.2 with a tentative date_scheduled populated AND
      // installer="" (unassigned). Material flag (rtsDate) is set when
      // material arrives. These are "tentatively dated, available to claim"
      // candidates the install manager can pull forward to fill partial-
      // capacity cells.
      //
      // Gates MIRROR Tier 1's pull-forward semantics minus the expediteFlag
      // requirement: same location, future date, material ready, fits caps
      // + stops + stores. Filter is location-based (any installer's job at
      // the cell's home location), NOT installer-based.
      //
      // Brandon's verified example: invoice 2520085 at BHM (status 1.2,
      // installer="", date_scheduled=5/29/26, rtsDate=5/20/26, expedite=
      // false, carpet 29.3 yds) → eligible for installer 0827.01 (cell
      // installer at BHM).
      if (doc.location !== homeLocation) continue;
      if (!isStrictlyAfterDate(doc.date_scheduled, cellDate)) continue;
      if (noMoreJobsAllowed) continue; // #146 — jobs-stops hard cap
      if (!isRtsReadyForCellDate(doc, cellDate)) continue;
      if (!installerHasApprovedStore(cellInstallerCandidate, doc.lowes_storeNo)) continue;
      alreadyScheduledJobs.push(
        toPopoverEntry(doc, carpetYds, furniturePieces, stepPieces)
      );
    }
  }

  expediteJobs.sort(closestFitCompare);
  rtsJobs.sort(closestFitCompare);
  alreadyScheduledJobs.sort(closestFitCompare);
  // Repairs sort: all carpetYds are synthetic = 1, so closestFitCompare
  // falls through to invoiceNoNumeric ASC tiebreak. Deterministic order.
  repairJobs.sort(closestFitCompare);

  return {
    expediteJobs,
    rtsJobs,
    alreadyScheduledJobs,
    repairJobs,
    totalCount:
      expediteJobs.length +
      rtsJobs.length +
      alreadyScheduledJobs.length +
      repairJobs.length,
  };
}
